import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { pendingCount, failedCount, replayQueue } from "@/lib/offline";
import { countOfflineFiles } from "@/lib/offlineFiles";
import { WifiOff, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Sticky connectivity strip. Shows when the device is offline (with the
 * count of changes waiting to sync) and auto-replays the offline write
 * queue the moment the connection comes back. Tapping it opens the
 * Pending sync screen so nothing is ever silently lost.
 */
export function OfflineBanner() {
  const nav = useNavigate();
  const [online, setOnline] = useState(() => (typeof navigator !== "undefined" ? navigator.onLine : true));
  const [pending, setPending] = useState(pendingCount());
  const [failed, setFailed] = useState(failedCount());
  const [fileCount, setFileCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const refreshCounts = async () => {
      setPending(pendingCount());
      setFailed(failedCount());
      setFileCount(await countOfflineFiles());
    };
    const sync = async () => {
      if (!navigator.onLine) return;
      if (pendingCount() === 0 && (await countOfflineFiles()) === 0) return;
      setSyncing(true);
      const { synced, failed: f } = await replayQueue(api);
      setSyncing(false);
      await refreshCounts();
      if (synced > 0) toast.success(`Back online — ${synced} change${synced > 1 ? "s" : ""} synced`);
      if (f > 0) toast.error(`${f} change${f > 1 ? "s" : ""} couldn't sync — tap the banner to review`);
    };
    const onOnline = () => { setOnline(true); sync(); };
    const onOffline = () => setOnline(false);
    const onChange = () => refreshCounts();
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener("fl-offline-change", onChange);
    window.addEventListener("fl-offline-files-change", onChange);
    // Catch queue left over from a previous session
    refreshCounts();
    sync();
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("fl-offline-change", onChange);
      window.removeEventListener("fl-offline-files-change", onChange);
    };
  }, []);

  const waiting = pending + fileCount;
  if (online && waiting === 0 && failed === 0 && !syncing) return null;

  return (
    <button
      onClick={() => nav("/app/sync")}
      className={`sticky top-0 z-50 w-full flex items-center justify-center gap-2 px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white ${failed > 0 && online ? "bg-amber-600" : online ? "bg-[#FF5F15]" : "bg-zinc-900"}`}
      data-testid="offline-banner"
    >
      {online ? (
        failed > 0 && waiting === 0 && !syncing ? (
          <>
            <AlertTriangle className="w-3.5 h-3.5" />
            {failed} change{failed > 1 ? "s" : ""} need{failed > 1 ? "" : "s"} attention — tap to review
          </>
        ) : (
          <>
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing offline changes…" : `${waiting} item${waiting > 1 ? "s" : ""} waiting to sync — tap to view`}
          </>
        )
      ) : (
        <>
          <WifiOff className="w-3.5 h-3.5" />
          Offline — you can keep working{waiting > 0 ? ` · ${waiting} item${waiting > 1 ? "s" : ""} saved on this phone` : ""}
        </>
      )}
    </button>
  );
}
